/**
 * Tree Undo Manager
 *
 * Thin wrapper around Y.UndoManager, scoped to the nodes of a
 * YjsDocManager (`undoScope()`: the v1 `nodes` map and the v2 `nodesV2` and
 * `deletedNodes` maps, so a map moved to schema v2 while open keeps undo). One manager instance is created per document (main or
 * shared) and kept alive for the session so switching docs within a session
 * preserves that doc's undo history. Page reload wipes everything.
 *
 * Design invariants:
 *   - Only transactions with origin === USER_ORIGIN are tracked. This
 *     guarantees that remote peers' edits, imports, migrations, orphan
 *     repair, layout caching, and consistency merges can never be undone.
 *     Per-user scoping is free: a shared doc's UndoManager only sees the
 *     local user's ops because remote updates use REMOTE_ORIGIN.
 *   - Rich-text note editing inside Tiptap editors goes through
 *     y-prosemirror's yUndoPlugin, which is a separate UndoManager bound
 *     to the XmlFragment. Both managers coexist without conflict.
 *   - captureTimeout=0: each user transaction becomes one undo step.
 *     Structure ops (drag, rename, create, delete) are individually tagged
 *     this way. Text editing is grouped at the word level inside tiptap
 *     (its default behavior).
 *
 * Lifecycle:
 *   - bind(docManager)   — create/return the manager for this doc.
 *   - setActive(docId)   — route Ctrl+Z/Y to this doc's manager.
 *   - clear()            — empty the active stack (call after import).
 *   - unbind(docId)      — destroy one manager (before doc.destroy()).
 *   - unbindAll()        — destroy all (on logout).
 */

import * as Y from "yjs";
import { emit, Events } from "./events.js";
import { USER_ORIGIN } from "../sync/origins.js";
import { createLogger } from "./logger.js";

const log = createLogger("Undo Manager");

// docId → Y.UndoManager
const managers = new Map();
// docId → unsubscribe fns (to clean up event listeners on unbind)
const listeners = new Map();

let activeManager = null;
let activeDocId = null;

function emitStateChange() {
  emit(Events.UNDO_STATE_CHANGED, {
    docId: activeDocId,
    canUndo: canUndo(),
    canRedo: canRedo(),
  });
}

/**
 * Create (or return existing) UndoManager for a YjsDocManager.
 * @param {{ docId: string, nodes: Y.Map, doc: Y.Doc, undoScope?: () => Y.AbstractType[] }} docManager
 * @returns {Y.UndoManager|null}
 */
export function bind(docManager) {
  if (!docManager || !docManager.doc || !docManager.nodes) {
    log.warn("bind: invalid doc manager");
    return null;
  }
  const docId = docManager.docId;
  const existing = managers.get(docId);
  if (existing) return existing;

  const scope = typeof docManager.undoScope === "function" ? docManager.undoScope() : docManager.nodes;
  const mgr = new Y.UndoManager(scope, {
    trackedOrigins: new Set([USER_ORIGIN]),
    captureTimeout: 0,
  });

  // Stamp every new undoable stack item with the wall-clock time it was
  // created. At Ctrl+Z time we scan all managers' top-of-stack timestamps
  // to pick the most recent edit regardless of the currently-active doc.
  // Redo follows the same pattern on the redoStack (Yjs re-populates it
  // after `undo()` and the stack item carries its meta back with it).
  const onAdded = (event) => {
    try { event.stackItem.meta.set("addedAt", Date.now()); } catch { /* meta readonly? ignore */ }
    if (docId === activeDocId) emitStateChange();
  };
  const onPopped = () => {
    if (docId === activeDocId) emitStateChange();
  };
  const onCleared = () => {
    if (docId === activeDocId) emitStateChange();
  };

  mgr.on("stack-item-added", onAdded);
  mgr.on("stack-item-popped", onPopped);
  mgr.on("stack-cleared", onCleared);
  // A node's notes are their own shared types: a delete adds them to what
  // undo covers before clearing them (yjs-doc.js _dropNotes), so undoing
  // the delete brings them back.
  docManager.extendUndoScope = (type) => mgr.addToScope(type);

  listeners.set(docId, () => {
    mgr.off("stack-item-added", onAdded);
    mgr.off("stack-item-popped", onPopped);
    mgr.off("stack-cleared", onCleared);
    if (docManager.extendUndoScope) docManager.extendUndoScope = null;
  });

  managers.set(docId, mgr);
  log.info("bound for", docId);
  return mgr;
}

/**
 * Destroy one UndoManager and its subscriptions.
 * Call BEFORE `docManager.doc.destroy()` so the UndoManager has a chance
 * to release its Y.Map subscriptions cleanly.
 */
export function unbind(docId) {
  const mgr = managers.get(docId);
  if (!mgr) return;
  const unlisten = listeners.get(docId);
  if (unlisten) unlisten();
  listeners.delete(docId);
  try {
    mgr.destroy();
  } catch (err) {
    log.warn("destroy failed (continuing):", err);
  }
  managers.delete(docId);
  if (activeDocId === docId) {
    activeManager = null;
    activeDocId = null;
    emitStateChange();
  }
  log.info("unbound", docId);
}

export function unbindAll() {
  for (const id of [...managers.keys()]) unbind(id);
}

/**
 * Route Ctrl+Z/Y to the manager for this doc.
 * @param {string|null} docId pass null for "no active doc".
 */
export function setActive(docId) {
  if (activeDocId === docId) return;
  activeDocId = docId;
  activeManager = docId ? managers.get(docId) || null : null;
  log.debug("active", docId);
  emitStateChange();
}

export function getActiveDocId() {
  return activeDocId;
}

export function canUndo() {
  if (activeManager && activeManager.undoStack.length > 0) return true;
  // Fall back to any tracked manager with undo history (temporal Ctrl+Z).
  for (const mgr of managers.values()) {
    if (mgr.undoStack.length > 0) return true;
  }
  return false;
}

export function canRedo() {
  if (activeManager && activeManager.redoStack.length > 0) return true;
  for (const mgr of managers.values()) {
    if (mgr.redoStack.length > 0) return true;
  }
  return false;
}

/**
 * Pick the manager whose top-of-stack has the newest `addedAt` stamp.
 * Ignores which doc is focused — "last action anywhere wins" is the rule
 * for undo/redo in a composited view where the user sees content from
 * many docs merged into one tree.
 * @param {Map<string, Y.UndoManager>} pool
 * @param {string} stackName - "undoStack" or "redoStack"
 * @returns {Y.UndoManager|null}
 */
function pickManagerByTimestamp(pool, stackName) {
  let bestMgr = null;
  let bestAt = -1;
  for (const mgr of pool.values()) {
    const stack = mgr[stackName];
    if (!stack || stack.length === 0) continue;
    const top = stack[stack.length - 1];
    const addedAt = top?.meta?.get?.("addedAt") ?? 0;
    if (addedAt > bestAt) {
      bestAt = addedAt;
      bestMgr = mgr;
    }
  }
  return bestMgr;
}

/**
 * Resolve the docId associated with a given manager (reverse lookup in the
 * managers map). O(n) but n is tiny (main + active shares + linked docs).
 */
function docIdOfManager(mgr) {
  for (const [id, m] of managers) if (m === mgr) return id;
  return null;
}

/**
 * Perform undo. Pure temporal: pick the manager whose top-of-undoStack has
 * the newest `addedAt` timestamp, regardless of which doc is "focused".
 * Maps to user intuition "undo my last action" — reverse chronological
 * order across every tracked doc (main + shares + linked docs).
 * @returns {boolean} true if something was undone.
 */
export function undo() {
  const target = pickManagerByTimestamp(managers, "undoStack");
  if (!target || target.undoStack.length === 0) return false;
  const before = {
    undo: target.undoStack.length,
    redo: target.redoStack.length,
  };
  let item;
  try {
    item = target.undo();
  } catch (err) {
    log.error("undo threw:", err);
    return false;
  }
  if (!item) return false;
  log.info(
    `undo docId=${docIdOfManager(target)} undoStack ${before.undo}→${target.undoStack.length} redoStack ${before.redo}→${target.redoStack.length}`,
  );
  emitStateChange();
  return true;
}

/**
 * Perform redo. Pure temporal, mirroring undo().
 */
export function redo() {
  const target = pickManagerByTimestamp(managers, "redoStack");
  if (!target || target.redoStack.length === 0) return false;
  const before = {
    undo: target.undoStack.length,
    redo: target.redoStack.length,
  };
  let item;
  try {
    item = target.redo();
  } catch (err) {
    log.error("redo threw:", err);
    return false;
  }
  if (!item) return false;
  log.info(
    `redo docId=${docIdOfManager(target)} undoStack ${before.undo}→${target.undoStack.length} redoStack ${before.redo}→${target.redoStack.length}`,
  );
  emitStateChange();
  return true;
}

/**
 * Empty the active manager's stacks. Call after import / full reset.
 */
export function clear() {
  if (!activeManager) return;
  activeManager.clear();
  log.info("cleared stacks for", activeDocId);
}

/**
 * Empty the stacks of a specific doc (even if not active).
 */
export function clearFor(docId) {
  const mgr = managers.get(docId);
  if (!mgr) return;
  mgr.clear();
  if (docId === activeDocId) emitStateChange();
}

/**
 * True when a manager exists for this doc.
 */
export function isBound(docId) {
  return managers.has(docId);
}
